import React from 'react';
import { OptimizationParams } from '../types';

interface OptimizationSettingsProps {
  params: OptimizationParams;
  onParamsChange: (params: OptimizationParams) => void;
}

const OptimizationSettings: React.FC<OptimizationSettingsProps> = ({ params, onParamsChange }) => {
  const handleChange = (key: keyof OptimizationParams, value: any) => {
    onParamsChange({ ...params, [key]: value });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Glide Ratio
        </label>
        <input
          type="number"
          step="0.1"
          value={params.glideRatio}
          onChange={(e) => handleChange('glideRatio', parseFloat(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-500 mt-1">e.g., 8.0 for 8:1 glide</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Start Altitude (m)
        </label>
        <input
          type="number"
          step="100"
          value={params.startAltitude}
          onChange={(e) => handleChange('startAltitude', parseFloat(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Terrain Clearance (m)
        </label>
        <input
          type="number"
          step="50"
          value={params.clearance}
          onChange={(e) => handleChange('clearance', parseFloat(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Expected Ceiling (m)
        </label>
        <input
          type="number"
          step="100"
          value={params.expectedCeiling}
          onChange={(e) => handleChange('expectedCeiling', parseFloat(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-500 mt-1">Maximum expected thermal altitude</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Optimization Density
        </label>
        <select
          value={params.optimizationDensity}
          onChange={(e) => handleChange('optimizationDensity', e.target.value as any)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="minimal">Minimal - Fewest thermals</option>
          <option value="conservative">Conservative - Fewer thermals</option>
          <option value="balanced">Balanced - Moderate thermals</option>
          <option value="aggressive">Aggressive - More thermals</option>
          <option value="very_aggressive">Very Aggressive - Most thermals</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          {params.optimizationDensity === 'minimal' && 'Very conservative thermal placement for maximum efficiency'}
          {params.optimizationDensity === 'conservative' && 'Conservative thermal placement for good efficiency'}
          {params.optimizationDensity === 'balanced' && 'Balanced thermal placement for optimal performance'}
          {params.optimizationDensity === 'aggressive' && 'Aggressive thermal placement for maximum climb opportunities'}
          {params.optimizationDensity === 'very_aggressive' && 'Very aggressive thermal placement for maximum coverage'}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Corridor Width (m)
        </label>
        <input
          type="number"
          step="1000"
          value={params.corridorWidth}
          onChange={(e) => handleChange('corridorWidth', parseFloat(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Base Speed (m/s)
        </label>
        <input
          type="number"
          step="0.5"
          value={params.baseSpeed}
          onChange={(e) => handleChange('baseSpeed', parseFloat(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Max Hotspots per Leg
        </label>
        <input
          type="number"
          min="1"
          max="10"
          value={params.maxHotspotsPerLeg}
          onChange={(e) => handleChange('maxHotspotsPerLeg', parseInt(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="insertVias"
          checked={params.insertVias}
          onChange={(e) => handleChange('insertVias', e.target.checked)}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="insertVias" className="ml-2 text-sm text-gray-700">
          Insert thermal waypoints
        </label>
      </div>
    </div>
  );
};

export default OptimizationSettings;
